/**
 * VÉRAULT Portal — Client · Itinerary (Jun 2026 · Portugal build)
 *
 * Renders the published itinerary snapshot — day-by-day, calm and easy
 * to read. Live location-exact weather, gentle status language, map and
 * official-link actions, and a Download Itinerary PDF control.
 * Advisor-only data never reaches this view (stripped server-side).
 */
import React from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Utensils, Compass, Plane, MapPin, Download, Loader2, ExternalLink,
  ChevronDown, Instagram, FileText, CalendarRange, LayoutList, Check,
} from "lucide-react";
import { api } from "../../../index";
import { getLocalDateISO } from "../../../lib/timezone";
import DayCountdownChip from "../../../components/DayCountdownChip";
import DayWeatherChip from "../../../components/DayWeatherChip";
import SunsetDecisionCard from "../../../components/SunsetDecisionCard";
import ImageLightbox from "../../../components/ImageLightbox";
import { pexelsSrcSet, pexelsAt, focalPosition, imageUrlOf } from "../../../utils/imageSrc";

const HERO_SIZES = "(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 900px";

const KIND_ICON = {
  hotel: Home, restaurant: Utensils, activity: Compass, tour: Compass,
  transfer: Plane, flight: Plane, note: MapPin,
};

const STATUS_META = {
  confirmed: { label: "Confirmed", cls: "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--foreground))]" },
  recommended: { label: "Advisor Recommended", cls: "border border-[hsl(var(--accent-gold))]/40 text-[hsl(var(--accent-gold))]" },
  optional: { label: "Optional", cls: "border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))]" },
  room_to_roam: { label: "Room to Roam", cls: "border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))]" },
  advisor_arranging: { label: "Advisor Arranging", cls: "border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-secondary))]" },
  decision_needed: { label: "Decision Needed", cls: "bg-[hsl(var(--foreground))]/5 text-[hsl(var(--foreground))]" },
  weather_dependent: { label: "Weather Dependent", cls: "border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))]" },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.16em] ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// Parse an itinerary item time string ("9:00 AM", "14:30") to minutes-since-midnight.
function timeToMinutes(t) {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return h * 60 + min;
}

// Given "today's" day, pick the item key closest to the current time in
// the trip's destination timezone (last event begun, else the first).
function pickNowItemKey(day, idx, tz) {
  const items = day?.items || [];
  if (!items.length) return null;
  const nowStr = new Date().toLocaleTimeString("en-GB", {
    timeZone: tz || "UTC", hour12: false, hour: "2-digit", minute: "2-digit",
  });
  const [nh, nm] = nowStr.split(":").map(Number);
  const nowMin = nh * 60 + nm;
  let chosen = -1;
  items.forEach((it, i) => {
    const mins = timeToMinutes(it.time);
    if (mins != null && mins <= nowMin) chosen = i;
  });
  if (chosen < 0) chosen = 0;
  const it = items[chosen];
  return `${idx}-${it.id || chosen}`;
}

function DayTimelineAccordion({ title, testid, children }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2 rounded-lg border border-[rgba(0,0,0,0.08)] bg-white/40 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--accent-gold))]/[0.04] transition-colors"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-secondary))]">{title}</span>
        <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28 }} className="overflow-hidden">
            <div className="px-3 pb-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ItemRow({ item, dayIdx, i, date, onExpand, isOpen, onToggle }) {  const Icon = KIND_ICON[item.kind] || MapPin;
  const timeBits = [item.time, item.end_time].filter(Boolean).join(" – ");
  const img = item.image;
  const imgUrl = imageUrlOf(img);
  const website = item.website || item.official_link || item.booking_link;
  const attachments = item.attachments || [];
  const hasDetail = imgUrl || website || item.instagram || item.map_url || item.address ||
    item.confirmation || attachments.length > 0 || item.description || item.note;
  const linkCls = "inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]";
  return (
    <li className="rounded-lg border border-transparent hover:border-[rgba(0,0,0,0.06)] hover:bg-white/40 transition-colors" data-testid={`client-itinerary-day-${dayIdx}-item-${i}`}>
      <button
        type="button"
        onClick={() => hasDetail && onToggle()}
        className={`w-full text-left flex items-start gap-2.5 p-2 ${hasDetail ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`client-itinerary-day-${dayIdx}-item-${i}-toggle`}
        aria-expanded={isOpen}
      >
        <Icon className="size-3.5 mt-1 text-[hsl(var(--foreground-muted))] shrink-0" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-[14.5px] leading-snug flex items-center gap-2 flex-wrap">
            {timeBits && <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{timeBits}</span>}
            <span>{item.title}</span>
            {item.weather && item.weather_dependent && (
              <DayWeatherChip weather={item.weather} date={date} compact testid={`client-itinerary-day-${dayIdx}-item-${i}-weather`} />
            )}
          </p>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <StatusChip status={item.status_label} />
            {item.location && (
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{item.location}</span>
            )}
            {item.confirmation && (
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]" data-testid={`client-itinerary-day-${dayIdx}-item-${i}-confirmation`}>
                Conf {item.confirmation}
              </span>
            )}
            {item.show_cost_to_client && item.client_cost && (
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-secondary))]">{item.client_cost}</span>
            )}
            {hasDetail && (
              <ChevronDown className={`size-3.5 text-[hsl(var(--foreground-muted))] ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
            )}
          </div>
          {!isOpen && (item.description || item.note) && (
            <p className="mt-1 font-serif text-[13px] leading-relaxed text-[hsl(var(--foreground-secondary))] line-clamp-2">
              {item.description || item.note}
            </p>
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }} className="overflow-hidden"
            data-testid={`client-itinerary-day-${dayIdx}-item-${i}-detail`}
          >
            <div className="pl-8 pr-2 pb-3 space-y-2.5">
              {imgUrl && (
                <button type="button" onClick={() => onExpand && onExpand(img)}
                  className="group block w-full overflow-hidden rounded-md" aria-label={`Expand image — ${item.title}`}>
                  <div className="aspect-[16/9] w-full overflow-hidden bg-[hsl(var(--background-paper))]">
                    <img src={pexelsAt(imgUrl, 720)} srcSet={pexelsSrcSet(imgUrl, [360, 720, 1080])}
                      sizes="(max-width: 640px) 90vw, 640px" alt={img?.alt || item.title || ""} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      style={{ objectPosition: focalPosition(img) }} />
                  </div>
                </button>
              )}
              {(item.description || item.note) && (
                <p className="font-serif text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{item.description || item.note}</p>
              )}
              {item.address && (
                <p className="flex items-start gap-1.5 font-serif text-[12.5px] text-[hsl(var(--foreground-secondary))]">
                  <MapPin className="size-3 mt-0.5 shrink-0 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />{item.address}
                </p>
              )}
              {item.confirmation && (
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">Confirmation · {item.confirmation}</p>
              )}
              {item.baby_note && (
                <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]" data-testid={`client-itinerary-day-${dayIdx}-item-${i}-baby`}>With Kenji — {item.baby_note}</p>
              )}
              <div className="flex items-center gap-3 flex-wrap pt-0.5">
                {item.map_url && (
                  <a href={item.map_url} target="_blank" rel="noreferrer noopener" className={linkCls} data-testid={`client-itinerary-day-${dayIdx}-item-${i}-map`}>
                    <MapPin className="size-3" strokeWidth={1.5} /> Map
                  </a>
                )}
                {website && (
                  <a href={website} target="_blank" rel="noreferrer noopener" className={linkCls} data-testid={`client-itinerary-day-${dayIdx}-item-${i}-official`}>
                    <ExternalLink className="size-3" strokeWidth={1.5} /> Official site
                  </a>
                )}
                {item.instagram && (
                  <a href={item.instagram} target="_blank" rel="noreferrer noopener" className={linkCls} data-testid={`client-itinerary-day-${dayIdx}-item-${i}-instagram`}>
                    <Instagram className="size-3" strokeWidth={1.5} /> Instagram
                  </a>
                )}
                {attachments.map((a, ai) => (
                  <a key={ai} href={a.url} target="_blank" rel="noreferrer noopener" className={linkCls} data-testid={`client-itinerary-day-${dayIdx}-item-${i}-attachment-${ai}`}>
                    <FileText className="size-3" strokeWidth={1.5} /> {a.name || "Document"}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function ClientItineraryView({ tripId: tripIdProp }) {
  const params = useParams();
  const tripId = tripIdProp || params.tripId;
  const [itin, setItin] = React.useState(null);
  const [state, setState] = React.useState("loading");
  const [downloading, setDownloading] = React.useState(false);
  const [sunsetDay, setSunsetDay] = React.useState(null);
  const [lightbox, setLightbox] = React.useState(null);
  const openImage = React.useCallback((img) => { if (imageUrlOf(img)) setLightbox(img); }, []);
  const [openItemKey, setOpenItemKey] = React.useState(null);
  const [showAllDays, setShowAllDays] = React.useState(false);
  const [openDayIdx, setOpenDayIdx] = React.useState(null);
  const destTz = itin?.destination_timezone || "UTC";
  const currentIdx = React.useMemo(() => {
    const dz = itin?.days || [];
    if (!dz.length) return 0;
    // A day is "today" when its date matches the local date in that day's
    // OWN timezone (per-segment aware for multi-country trips).
    const exact = dz.findIndex((d) => d.date === getLocalDateISO(d.timezone || destTz));
    if (exact >= 0) return exact;
    const today = getLocalDateISO(destTz);
    const future = dz.findIndex((d) => (d.date || "") >= today);
    return future >= 0 ? future : dz.length - 1;
  }, [itin, destTz]);
  React.useEffect(() => { setOpenDayIdx((prev) => (prev === null ? currentIdx : prev)); }, [currentIdx]);

  // On first load (or arriving via a #day-N deep link from the Overview),
  // open the right day, expand the item nearest the current time, and
  // scroll it into view beneath the sticky navs.
  const didFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (state !== "ready" || didFocusRef.current) return;
    const dz = itin?.days || [];
    if (!dz.length) return;
    didFocusRef.current = true;
    const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
    const m = hash.match(/day-(\d+)/);
    const targetIdx = m
      ? Math.min(dz.length - 1, Math.max(0, parseInt(m[1], 10) - 1))
      : null;
    const focusIdx = targetIdx != null ? targetIdx : currentIdx;
    setShowAllDays(false);
    setOpenDayIdx(focusIdx);
    if (targetIdx == null && dz[currentIdx]?.date === getLocalDateISO(dz[currentIdx]?.timezone || destTz)) {
      const key = pickNowItemKey(dz[currentIdx], currentIdx, dz[currentIdx]?.timezone || destTz);
      if (key) setOpenItemKey(key);
    }
    setTimeout(() => {
      const el =
        document.querySelector(`[data-testid='client-itinerary-day-${focusIdx}']`) ||
        document.querySelector(`[data-testid='client-itinerary-day-${focusIdx}-locked']`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => window.scrollBy({ top: -120, left: 0, behavior: "smooth" }), 380);
      }
    }, 140);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const isAdvisorPreview =
    typeof window !== "undefined" && (window.location.pathname || "").includes("/portal/advisor/");

  React.useEffect(() => {
    let alive = true;
    const path = isAdvisorPreview
      ? `/design-studio/${tripId}/sunset-decision`
      : `/client-portal/trips/${tripId}/sunset-decision`;
    api.get(path).then(({ data }) => {
      if (alive && data?.enabled) setSunsetDay(data.day_date);
    }).catch(() => {});
    return () => { alive = false; };
  }, [tripId, isAdvisorPreview]);

  React.useEffect(() => {
    let alive = true;
    const path = isAdvisorPreview
      ? `/design-studio/${tripId}/itinerary`
      : `/client-portal/trips/${tripId}/design-studio-itinerary`;
    api.get(path).then(({ data }) => {
      if (!alive) return;
      if (data?.status !== "published") { setState("not-published"); return; }
      setItin(data); setState("ready");
    }).catch((err) => {
      if (!alive) return;
      if (err?.response?.status === 404) setState("not-published");
      else setState("error");
    });
    return () => { alive = false; };
  }, [tripId, isAdvisorPreview]);

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const path = isAdvisorPreview
        ? `/design-studio/${tripId}/itinerary.pdf`
        : `/client-portal/trips/${tripId}/itinerary.pdf`;
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "verault-itinerary.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // quiet failure — control stays available
    } finally {
      setDownloading(false);
    }
  };

  if (state === "loading") return <p className="py-20 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]" data-testid="client-itinerary-loading">Loading your itinerary…</p>;
  if (state === "not-published") return <div className="py-20 text-center max-w-md mx-auto" data-testid="client-itinerary-pending"><p className="font-serif text-[20px]">Your itinerary is being finalized.</p><p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">Your advisor will share it shortly.</p></div>;
  if (state === "error" || !itin) return <p className="py-20 text-center font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]" data-testid="client-itinerary-error">Couldn&apos;t open the itinerary right now.</p>;

  const days = itin.days || [];
  const cover = itin.cover_image;
  const coverUrl = imageUrlOf(cover) || itin.cover_image_url;

  return (
    <article className="max-w-4xl mx-auto space-y-10" data-testid="client-itinerary">
      {coverUrl && (
        <div className="relative -mx-4 sm:-mx-6 lg:-mx-12 -mt-6 mb-2">
          <div className="aspect-[21/9] sm:aspect-[16/6] w-full overflow-hidden bg-[hsl(var(--background-paper))]">
            <img
              src={pexelsAt(coverUrl, 1600)}
              srcSet={pexelsSrcSet(coverUrl)}
              sizes="100vw"
              alt={cover?.alt || itin.headline || "Trip cover"}
              loading="eager"
              fetchpriority="high"
              className="w-full h-full object-cover"
              style={{ objectPosition: focalPosition(cover) }}
              data-testid="client-itinerary-cover"
            />
          </div>
          {(cover?.credit || itin.cover_image_credit) && (
            <p className="absolute bottom-1.5 right-3 font-mono text-[8.5px] uppercase tracking-[0.14em] text-white/70 drop-shadow">
              {cover?.credit || itin.cover_image_credit}
            </p>
          )}
        </div>
      )}
      <header className="text-center space-y-3 pb-6 border-b border-[rgba(0,0,0,0.06)]">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">Itinerary</p>
        <h1 className="font-serif text-[28px] sm:text-[34px] leading-tight">{itin.headline || "Your VÉRAULT itinerary."}</h1>
        <div className="pt-1">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[rgba(0,0,0,0.1)] bg-white/60 hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] transition-colors disabled:opacity-50"
            data-testid="itinerary-download-pdf"
          >
            {downloading ? <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} /> : <Download className="size-3.5" strokeWidth={1.5} />}
            Download Itinerary PDF
          </button>
        </div>
      </header>

      {days.length > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="itinerary-day-view-toggle">
          <button type="button" onClick={() => { setShowAllDays(false); setOpenDayIdx(currentIdx); }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-[9.5px] uppercase tracking-[0.18em] transition-colors ${!showAllDays ? "border-[hsl(var(--accent-gold))] text-[hsl(var(--accent-gold))]" : "border-[rgba(0,0,0,0.1)] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))]/50"}`}
            data-testid="itinerary-view-one">
            <CalendarRange className="size-3" strokeWidth={1.5} /> One day at a time
          </button>
          <button type="button" onClick={() => setShowAllDays(true)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-[9.5px] uppercase tracking-[0.18em] transition-colors ${showAllDays ? "border-[hsl(var(--accent-gold))] text-[hsl(var(--accent-gold))]" : "border-[rgba(0,0,0,0.1)] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))]/50"}`}
            data-testid="itinerary-view-all">
            <LayoutList className="size-3" strokeWidth={1.5} /> Show every day
          </button>
        </div>
      )}

      {/* What's next — swipeable carousel of the days ahead */}
      {days.length > 1 && (() => {
        const upcoming = days.map((d, i) => ({ d, i })).filter(({ i }) => i >= currentIdx);
        const cards = (upcoming.length ? upcoming : days.map((d, i) => ({ d, i }))).slice(0, 8);
        return (
          <div data-testid="itinerary-whats-next">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">What's coming next</p>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
              {cards.map(({ d, i }) => {
                const u = imageUrlOf(d.hero_image) || d.hero_image_url;
                return (
                  <button key={d.id || i} type="button"
                    onClick={() => { setShowAllDays(false); setOpenDayIdx(i); setTimeout(() => { const el = document.querySelector(`[data-testid='client-itinerary-day-${i}']`); if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setTimeout(() => window.scrollBy({ top: -120, left: 0, behavior: "smooth" }), 380); } }, 60); }}
                    className="snap-start shrink-0 w-[190px] text-left rounded-lg overflow-hidden border border-[rgba(0,0,0,0.08)] bg-white hover:border-[hsl(var(--accent-gold))]/50 transition-colors"
                    data-testid={`whats-next-card-${i}`}>
                    <div className="aspect-[16/10] w-full overflow-hidden bg-[hsl(var(--background-paper))]">
                      {u && <img src={pexelsAt(u, 400)} alt={d.title || ""} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: focalPosition(d.hero_image) }} />}
                    </div>
                    <div className="p-2.5">
                      <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))]">
                        {i === currentIdx ? "Today" : `Day ${i + 1}`}{d.date ? ` · ${new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                      </p>
                      <p className="font-serif text-[13px] leading-snug mt-0.5 line-clamp-2">{d.title || "Untitled day"}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] mt-1">All times shown are local · Lisbon (WEST)</p>
          </div>
        );
      })()}
      {days.length === 0 ? (
        <p className="text-center font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">Day-by-day details coming shortly.</p>
      ) : (
        <ol className="space-y-12">
          {days.map((d, idx) => {
            const titleHasDay = /^Day\s+\d/i.test(d.title || "");
            const unlockMs = d.day_unlock_at ? new Date(d.day_unlock_at).getTime() : null;
            const isLocked = (d.published === false) || (unlockMs && unlockMs > Date.now());
            if (isLocked) {
              return (
                <motion.li key={d.id || idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                  className="space-y-3 py-6 px-5 rounded-lg border border-dashed border-[rgba(0,0,0,0.12)] bg-[hsl(var(--background-paper))]/30"
                  data-testid={`client-itinerary-day-${idx}-locked`}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Day {idx + 1}</span>
                    {d.date && <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{new Date(d.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>}
                    {unlockMs && <DayCountdownChip unlockAt={d.day_unlock_at} dayIdx={idx} />}
                  </div>
                  <h2 className="font-serif text-[20px] sm:text-[22px] leading-tight text-[hsl(var(--foreground-muted))]">{d.title || "Untitled day"}</h2>
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                    {unlockMs
                      ? "This day will be revealed when the moment arrives — your advisor wants you to stay present in the day before."
                      : "Your advisor is still finalizing this day."}
                  </p>
                </motion.li>
              );
            }
            const dayOpen = showAllDays || openDayIdx === idx;
            const isPast = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const compactPast = isPast && !dayOpen;
            return (
              <motion.li key={d.id || idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                className={`space-y-4 transition-opacity ${compactPast ? "opacity-60 hover:opacity-90" : ""}`} data-testid={`client-itinerary-day-${idx}`} data-open={dayOpen ? "true" : "false"} data-day-phase={isPast ? "past" : isCurrent ? "current" : "future"}>
                <button type="button"
                  onClick={() => { setShowAllDays(false); setOpenDayIdx(dayOpen && !showAllDays ? null : idx); }}
                  className={`w-full text-left group ${isCurrent ? "rounded-lg bg-[hsl(var(--accent-gold))]/[0.05] border border-[hsl(var(--accent-gold))]/30 px-4 py-3.5" : ""}`}
                  data-testid={`client-itinerary-day-${idx}-header`} aria-expanded={dayOpen}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    {!titleHasDay && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Day {idx + 1}</span>
                    )}
                    {d.date && <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>}
                    {d.location_label && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">· {d.location_label}</span>}
                    {isCurrent && <span className="font-mono text-[8.5px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]">Today</span>}
                    {compactPast && <span className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">Completed</span>}
                    <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] ml-auto transition-transform ${dayOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap mt-1">
                    <h2 className={`font-serif leading-tight ${compactPast ? "text-[16px] sm:text-[17px] text-[hsl(var(--foreground-muted))]" : "text-[22px] sm:text-[26px]"}`}>{d.title || "Untitled day"}</h2>
                    {d.weather && !compactPast && (
                      <DayWeatherChip weather={d.weather} date={d.date} compact testid={`client-itinerary-day-${idx}-weather`} />
                    )}
                  </div>
                  {(d.day_style || d.theme) && !compactPast && (
                    <p className="font-accent italic text-[14px] text-[hsl(var(--accent-gold))] mt-1" data-testid={`client-itinerary-day-${idx}-style`}>{d.day_style || d.theme}</p>
                  )}
                </button>
                <AnimatePresence initial={false}>
                {dayOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <div className="space-y-4 pt-1">
                {(d.hero_image || d.hero_image_url) ? (() => {
                  const dImg = d.hero_image;
                  const dUrl = imageUrlOf(dImg) || d.hero_image_url;
                  if (!dUrl) return null;
                  return (
                    <figure className="space-y-1">
                      <button
                        type="button"
                        onClick={() => openImage(dImg || { url: dUrl, alt: d.title })}
                        className="group block w-full overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-gold))]/50"
                        data-testid={`client-itinerary-day-${idx}-hero`}
                        aria-label={`Expand image — ${d.title || "day"}`}
                      >
                        <div className="aspect-[16/9] sm:aspect-[16/8] w-full overflow-hidden bg-[hsl(var(--background-paper))]">
                          <img
                            src={pexelsAt(dUrl, 1024)}
                            srcSet={pexelsSrcSet(dUrl)}
                            sizes={HERO_SIZES}
                            alt={dImg?.alt || d.title || ""}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                            style={{ objectPosition: focalPosition(dImg) }}
                          />
                        </div>
                      </button>
                      {(dImg?.caption || dImg?.credit) && (
                        <figcaption className="flex items-center justify-between gap-2 px-0.5">
                          {dImg?.caption && <span className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">{dImg.caption}</span>}
                          {dImg?.credit && <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] ml-auto">{dImg.credit}</span>}
                        </figcaption>
                      )}
                    </figure>
                  );
                })() : null}
                {d.description && (
                  <p className="font-serif text-[16px] leading-relaxed text-[hsl(var(--foreground))]">{d.description}</p>
                )}
                {d.room_to_roam && (
                  <p className="font-accent italic text-[13.5px] leading-relaxed text-[hsl(var(--foreground-muted))] pl-3 border-l-2 border-[hsl(var(--accent-gold))]/30" data-testid={`client-itinerary-day-${idx}-roam`}>
                    <span className="font-mono not-italic text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] mr-2">Room to Roam</span>
                    {d.room_to_roam}
                  </p>
                )}
                {d.client_facing_notes && (
                  <p className="font-serif text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]" data-testid={`client-itinerary-day-${idx}-notes`}>
                    {d.client_facing_notes}
                  </p>
                )}
                {d.farewell_note && (
                  <div className="rounded-lg border border-[hsl(var(--accent-gold))]/25 bg-[hsl(var(--accent-gold))]/[0.05] px-5 py-5 sm:px-7 sm:py-6" data-testid={`client-itinerary-day-${idx}-farewell`}>
                    <p className="font-accent italic text-[15px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground-secondary))] whitespace-pre-line">
                      {d.farewell_note}
                    </p>
                  </div>
                )}
                {Array.isArray(d.departure_snapshot) && d.departure_snapshot.length > 0 && (
                  <div className="rounded-lg border border-[rgba(0,0,0,0.08)] bg-white/60 px-4 py-3.5" data-testid={`client-itinerary-day-${idx}-snapshot`}>
                    <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] mb-2.5">Departure Snapshot</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {d.departure_snapshot.map((s, si) => (
                        <div key={si} className="flex flex-col">
                          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">{s.label}</span>
                          <span className="font-serif text-[17px] leading-tight mt-0.5">{s.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(() => {
                  const collapse = d.collapse_timeline;
                  const timelineTitle = d.timeline_title || "View plan & details";
                  const itemsBlock = (d.items || []).length > 0 && (
                    <ul className="space-y-2 mt-2">
                      {d.items.map((it, i) => {
                        const key = `${idx}-${it.id || i}`;
                        return (
                          <ItemRow key={it.id || i} item={it} dayIdx={idx} i={i} date={d.date} onExpand={openImage}
                            isOpen={openItemKey === key}
                            onToggle={() => setOpenItemKey((cur) => (cur === key ? null : key))} />
                        );
                      })}
                    </ul>
                  );
                  const checklistBlock = Array.isArray(d.final_checklist) && d.final_checklist.length > 0 && (
                    <div className="mt-4 rounded-lg border border-[rgba(0,0,0,0.08)] bg-[hsl(var(--background-paper))]/40 px-4 py-3.5" data-testid={`client-itinerary-day-${idx}-checklist`}>
                      <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">Final sweep before you go</p>
                      <ul className="space-y-1.5">
                        {d.final_checklist.map((c, ci) => (
                          <li key={ci} className="flex items-start gap-2 font-serif text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
                            <Check className="size-3.5 mt-0.5 shrink-0 text-[hsl(var(--accent-gold))]" strokeWidth={1.6} />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                  if (!collapse) {
                    return (<>{itemsBlock}{checklistBlock}</>);
                  }
                  return (
                    <DayTimelineAccordion title={timelineTitle} testid={`client-itinerary-day-${idx}-timeline-accordion`}>
                      {itemsBlock}
                      {checklistBlock}
                    </DayTimelineAccordion>
                  );
                })()}
                {sunsetDay && d.date === sunsetDay && (
                  <div className="mt-4">
                    <SunsetDecisionCard tripId={tripId} />
                  </div>
                )}
                </div>
                </motion.div>
                )}
                </AnimatePresence>
              </motion.li>
            );
          })}
        </ol>
      )}

      <footer className="pt-6 border-t border-[rgba(0,0,0,0.06)] text-center">
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
          Forecasts are live and location-specific · Luxury is peace.
        </p>
      </footer>

      <ImageLightbox image={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </article>
  );
}
